import React, { FunctionComponent } from 'react'
import { useTranslation } from 'react-i18next'

import {
  StyledFullWidthButton,
  StyledRealWorldDataCard,
  StyledRealWorldDataCardContent,
  StyledOverlayText,
  StyledEnrolledIndicator,
  StyledRealWorldDataCardImage,
  StyledNomineeDetails,
  StyledLearnMoreLink
} from './RealWorldDataCardPatientDetails.styles'
import { ClinTheme } from '../../ClinTheme'
import { ClinIcon } from '../../components/ClinIcon'
import { ClinIconPathName } from '../../components/ClinIcon/ClinIcon.paths'
import { ClinSpacer } from '../../components/ClinSpacer'
import { ClinText } from '../../components/ClinText'
import { useAppContext } from '../../context/app'
import { PatientDetailsDto } from '../../types/swaggerTypes'
import {
  openCastorEdcUrl,
  openExternalUrl
} from '../../utils/rwdFollowUpEligibility'

interface RealWorldDataCardPatientDetailsProps {
  patient?: PatientDetailsDto
  hasShippedOrder?: boolean
  castorEdcUrl?: string | null
}

const RealWorldDataCardPatientDetails: FunctionComponent<
  RealWorldDataCardPatientDetailsProps
> = ({ patient = null, hasShippedOrder = false, castorEdcUrl }) => {
  const { t } = useTranslation()
  const { user, userDetails } = useAppContext()
  const buttonId = 'learn-more-rwd-button'

  const isEnrolled = patient?.realWorldDataEnrolFlag === 'Y'

  const handleLearnMoreClick = () => {
    document.getElementById(buttonId)?.blur()
    openExternalUrl('/programs/access-programs/real-world-data')
  }

  const handleSubmitFollowUpClick = () => {
    openCastorEdcUrl(
      castorEdcUrl ?? patient?.castorEdcUrl,
      user?.profile.email ?? userDetails?.contactCard.email
    )
  }

  const capitalize = (str: string) => {
    if (!str) return str
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
  }

  const displayNomineeDetails = () => {
    return (
      patient != null &&
      patient.rwdNominationDetails != null &&
      (patient.rwdNominationDetails.classification !== '' ||
        patient.rwdNominationDetails.emailAddress !== '' ||
        patient.rwdNominationDetails.fullName !== '')
    )
  }

  // AC1 & AC2: enrolled + has shipped order -> show "Submit data for this patient" CTA with Castor EDC deep-link
  // nominee details shown only when nominee exists (AC1), hidden otherwise (AC2)
  const showSubmitFollowUp = isEnrolled && hasShippedOrder

  // AC3: program open for RWD but patient not enrolled -> show "Learn More" CTA
  const showLearnMore = !isEnrolled

  const hasButtons = true

  return (
    <StyledRealWorldDataCard>
      <StyledRealWorldDataCardImage isEnrolled={isEnrolled}>
        <StyledOverlayText
          onClick={hasButtons ? undefined : handleLearnMoreClick}
          isClickable={!hasButtons}
        >
          <ClinText>{t('access_programs:rwd_button_titile')}</ClinText>
        </StyledOverlayText>
      </StyledRealWorldDataCardImage>
      <StyledRealWorldDataCardContent>
        {!isEnrolled && (
          <ClinText
            fontSize={ClinTheme.fontSizes[2]}
            fontWeight={ClinTheme.fontWeights.bold}
            textAlign="center"
          >
            {t('access_programs:card_title')}
          </ClinText>
        )}
        {isEnrolled && (
          <>
            <ClinText>
              {t('access_programs:enroll_info_text')}
              <br></br>
              <StyledLearnMoreLink onClick={handleLearnMoreClick}>
                {t('access_programs:enroll_url_text')}
              </StyledLearnMoreLink>
            </ClinText>
            <ClinSpacer height={ClinTheme.space[3]} />
          </>
        )}
        {isEnrolled && (
          <StyledEnrolledIndicator>
            <ClinIcon
              iconName={ClinIconPathName.CheckFilled}
              iconSize={18}
              viewBox="0 0 20 20"
              iconHeight={20}
            ></ClinIcon>
            <ClinText>{t('access_programs:enroll_title')}</ClinText>
          </StyledEnrolledIndicator>
        )}
        <ClinSpacer height={ClinTheme.space[3]} />
        {/* AC1: enrolled with nominee - show nominee details */}
        {showSubmitFollowUp && displayNomineeDetails() && (
          <StyledNomineeDetails>
            <ClinText
              color={ClinTheme.colors.primaryDark}
              fontWeight={ClinTheme.fontWeights.bold}
            >
              {t('access_programs:nominee_details')}
            </ClinText>
            <ClinSpacer height={ClinTheme.space[1]} />
            <ClinText>{patient?.rwdNominationDetails?.fullName}</ClinText>
            <ClinText>
              {capitalize(patient?.rwdNominationDetails?.classification || '')}
            </ClinText>
            <ClinText>{patient?.rwdNominationDetails?.emailAddress}</ClinText>
            <ClinSpacer height={ClinTheme.space[3]} />
          </StyledNomineeDetails>
        )}
        {/* AC1 & AC2: enrolled + shipped order -> "Submit data for this patient".
            Deep-links to patient record when castorEdcUrl is present,
            falls back to Castor EDC homepage when no patient deep-link is available. */}
        {showSubmitFollowUp && (
          <>
            <StyledFullWidthButton
              variant="secondaryCompact"
              onClick={handleSubmitFollowUpClick}
            >
              {t('access_programs:submit_follow_up_button')}
            </StyledFullWidthButton>
            <ClinSpacer height={ClinTheme.space[3]} />
          </>
        )}
        {/* AC3: not enrolled - time-to-read label + "Learn More" CTA */}
        {showLearnMore && (
          <>
            <ClinText
              color={ClinTheme.colors.darkGrey}
              fontSize={ClinTheme.fontSizes[0]}
              fontWeight={ClinTheme.fontWeights.medium}
              textTransform="uppercase"
            >
              {t('access_programs:time_to_read')}
            </ClinText>
            <ClinSpacer height={ClinTheme.space[3]} />
          </>
        )}
        {showLearnMore && (
          <>
            <StyledFullWidthButton
              variant="secondaryCompact"
              id={buttonId}
              onClick={handleLearnMoreClick}
            >
              {t('access_programs:learn_more')}
            </StyledFullWidthButton>
            <ClinSpacer height={ClinTheme.space[3]} />
          </>
        )}
      </StyledRealWorldDataCardContent>
    </StyledRealWorldDataCard>
  )
}

export default RealWorldDataCardPatientDetails
